import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  DestroyRef,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faEdit,
  faTimes,
  faSave,
  faDownload,
  faIdCard,
  faStar,
  faBriefcase,
  faGraduationCap,
  faLanguage,
  faPlane,
  faSmile,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, Observable, of } from 'rxjs';

import { StudentProfile } from '../../../api/models/student-profile';
import { WorkExperience } from '../../../api/models/work-experience';
import { Education, LanguageProficiency } from 'src/app/api/models';
import { StudentProfileService } from '../../../api/services/student-profile.service';
import { InterestService } from '../../../shared/services/interest.service';
import { LocaleService } from '../../../shared/services/locale.service';
import { ModalService } from '../../../shared/services/modal.service';
import { AlertService, alertMessages } from '../../../shared/services/alert.service';
import { LanguageProficiencyUtils } from '../../../shared/utils/student-profile.utils';
import { InputBirthdateComponent } from '../../../core/form-input-birthdate/input-birthdate.component';
import { ProfileTabCardComponent } from './profile-tab-card.component';
import { ModalStudentProfileInterestsComponent } from '../../../student/student-profile/modal-student-profile-interests/modal-student-profile-interests.component';
import { ModalStudentProfileWorkexperienceComponent } from '../../../student/student-profile/modal-student-profile-workexperience/modal-student-profile-workexperience.component';
import { ModalStudentProfileEducationComponent } from '../../../student/student-profile/modal-student-profile-education/modal-student-profile-education.component';
import { ModalStudentProfileLanguageComponent } from '../../../student/student-profile/modal-student-profile-language/modal-student-profile-language.component';
import { ModalStudentProfileForeignexperienceComponent } from '../../../student/student-profile/modal-student-profile-foreignexperience/modal-student-profile-foreignexperience.component';
import { ModalStudentProfileHobbyComponent } from '../../../student/student-profile/modal-student-profile-hobby/modal-student-profile-hobby.component';
import { ModalStudentProfileResumeComponent } from '../../../student/student-profile/modal-student-profile-resume/modal-student-profile-resume.component';

@Component({
  selector: 'app-student-cv-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    InputBirthdateComponent,
    ProfileTabCardComponent,
  ],
  templateUrl: './student-cv-tab.component.html',
})
export class StudentCvTabComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly modal = inject(ModalService);
  private readonly alertService = inject(AlertService);
  private readonly studentProfileService = inject(StudentProfileService);
  readonly interestService = inject(InterestService);
  readonly locale = inject(LocaleService);

  entity = input.required<StudentProfile>();
  profileChanged = output<void>();

  readonly LanguageProficiencyUtils = LanguageProficiencyUtils;

  // Local mutable copy for inline personal details editing
  readonly editableEntity = signal<StudentProfile>({} as StudentProfile);

  constructor() {
    // Sync from parent input whenever it changes
    effect(() => {
      this.editableEntity.set({ ...this.entity() });
    });
  }

  protected readonly faEdit = faEdit;
  protected readonly faTimes = faTimes;
  protected readonly faSave = faSave;
  protected readonly faDownload = faDownload;
  protected readonly faIdCard = faIdCard;
  protected readonly faStar = faStar;
  protected readonly faBriefcase = faBriefcase;
  protected readonly faGraduationCap = faGraduationCap;
  protected readonly faLanguage = faLanguage;
  protected readonly faPlane = faPlane;
  protected readonly faSmile = faSmile;
  protected readonly faFileLines = faFileLines;

  trackById = (_index: number, item: { id?: string | null }): string =>
    item.id ?? `index-${_index}`;

  updateEntityField(field: keyof StudentProfile, value: any): void {
    this.editableEntity.update((e) => ({ ...e, [field]: value }));
  }

  updateStudentprofile() {
    const current = this.editableEntity();
    if (!current.id) return;

    this.modal.Loader(true);
    this.studentProfileService
      .updateStudentProfile({ id: current.id!, body: current })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.modal.Loader(false);
        this.alertService.success(alertMessages.successDataSaved);
        this.profileChanged.emit();
      });
  }

  editInterests() {
    const dataProfile = Object.assign({}, this.entity());
    this.modal
      .Modal(ModalStudentProfileInterestsComponent, 'Field of interests', '', dataProfile, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  addWorkexperience() {
    const data = {
      profile_id: this.entity().id!,
      workExperience: { id: '', title: '', companyName: '', companyUrl: '', location: '', jobTitle: '', description: '', startDate: null, endDate: null, isCurrent: false, areaOfInterests: [] },
    };
    this.modal
      .Modal(ModalStudentProfileWorkexperienceComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  updateWorkexperience(workExperience: WorkExperience): void {
    const data = { profile_id: this.entity().id!, workExperience };
    this.modal
      .Modal(ModalStudentProfileWorkexperienceComponent, '', '', data, [], { size: 'xl' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteWorkexperience(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteWorkExperience({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteWorkExperience', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  addEducation() {
    const data = {
      profile_id: this.entity().id!,
      education: <Education>{ schoolId: null, educationLevelId: null, schoolName: '', schoolCity: '', schoolCountry: '', programName: '', level: '', profile: '', phase: '', description: '', startDate: null, endDate: null, isCurrent: false, graduated: false, areaOfInterests: [] },
    };
    this.modal
      .Modal(ModalStudentProfileEducationComponent, '', '', data, [], { size: 'xl' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  updateEducation($education: any) {
    const data = { profile_id: this.entity().id!, education: $education };
    this.modal
      .Modal(ModalStudentProfileEducationComponent, '', '', data, [], { size: 'xl' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteEducation(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteEducation({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteEducation', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  addLanguage() {
    const data = {
      profile_id: this.entity().id!,
      languageProficiency: <LanguageProficiency>{ id: '', languageCode: '', speakingProficiency: 0, readingProficiency: 0, writingProficiency: 0 },
    };
    this.modal
      .Modal(ModalStudentProfileLanguageComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  updateLanguage($languageProficiency: any) {
    const data = { profile_id: this.entity().id!, languageProficiency: $languageProficiency };
    this.modal
      .Modal(ModalStudentProfileLanguageComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteLanguage(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteLanguageProficiency({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteLanguageProficiency', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  addForeignexperience() {
    const data = {
      profile_id: this.entity().id!,
      foreignExperience: { id: '', title: '', description: '', companyInformation: '', startDate: null, endDate: null, country: '' },
    };
    this.modal
      .Modal(ModalStudentProfileForeignexperienceComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  updateForeignexperience(foreignExperience: any) {
    const data = { profile_id: this.entity().id!, foreignExperience };
    this.modal
      .Modal(ModalStudentProfileForeignexperienceComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteForeignexperience(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteForeignExperience({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteForeignExperience', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  addInterest() {
    const data = { profile_id: this.entity().id!, interest: { id: '', description: '' } };
    this.modal
      .Modal(ModalStudentProfileHobbyComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  updateInterest(interest: any) {
    const data = { profile_id: this.entity().id!, interest };
    this.modal
      .Modal(ModalStudentProfileHobbyComponent, '', '', data, [], { size: 'xl', backdrop: 'static' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteInterest(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteInterest({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteInterest', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  addResume() {
    const data = Object.assign({}, this.entity());
    this.modal
      .Modal(ModalStudentProfileResumeComponent, '', '', data, [], { backdrop: 'static', size: 'xl' })
      .then((result) => { if (result === true) this.profileChanged.emit(); })
      .catch(() => {});
  }

  deleteResume(id: string) {
    if (!this.entity().id) return;
    this.modal.Confirm('Delete', 'Are you sure you want to delete this?')
      .then((confirm) => {
        if (confirm === true) {
          this.studentProfileService.deleteCv({ profileId: this.entity().id!, id })
            .pipe(catchError(this.handleError('deleteCv', false)), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => { this.alertService.success(alertMessages.successDataRemoved); this.profileChanged.emit(); });
        }
      }).catch(() => {});
  }

  private handleError<T>(operation: string = '', result?: T) {
    return (_error: any): Observable<T> => {
      this.modal.Alert('Error', 'Something went wrong, please try again');
      return of(result as T);
    };
  }
}
